import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import QRCode from 'react-qr-code';
import { apiService } from '../services/apiService';
import { useTvFreshness, tvLoadErrorMessage } from '../hooks/useTvFreshness';
import { useOnReconnect } from '../hooks/useOnReconnect';
import { useTvStage } from '../hooks/useTvStage';
import { formatSavedAt } from '../services/connectionStatus';
import { readTvSnapshot, writeTvSnapshot } from '../utils/tvSnapshot';
import { getCategoryLabel } from '../utils/eventHelpers';
import { eventImage, monogram } from '../components/EventsBrowse';
import './EventsTv.css';

/**
 * The events screen: what is on at the club over the next seven days.
 *
 * Runs unattended on a club TV, public and signed out, at a fixed 1920x1080
 * stage scaled to the panel (hooks/useTvStage.js). Today is always the first
 * row. One event at a time is featured on the left, rotating through the week,
 * with a QR code that opens its RSVP page on a phone.
 *
 * Designed in Claude Design as "Events TV". The design's tweakable props are
 * URL parameters here, so a signage player can be set up without a code change:
 *
 *   ?rotate=12     seconds each featured event stays up (3 to 60)
 *   ?qr=0          hide the "Scan to RSVP" strip
 *   ?empty=hide    leave out days with nothing on
 *   ?ticker=1      add a "Coming up" ticker of events after this week
 */

const DAY = 86400000;
const REFRESH_INTERVAL_MS = 60000;
// The left panel is fixed at 820 and the day rows need room for a time, a title and a category.
const MIN_STAGE_WIDTH = 1600;
const TICKER_LENGTH = 6;
const SNAPSHOT_KEY = 'events-tv';

const timeLabel = (date) => date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
const shortDate = (date) => date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

const startOfDay = (date) => {
  const day = new Date(date);
  day.setHours(0, 0, 0, 0);
  return day;
};

/** TODAY, TOMORROW, or the weekday, for the badge on the featured event. */
function whenLabel(date, today) {
  const diff = Math.floor((startOfDay(date) - today) / DAY);
  if (diff === 0) return 'TODAY';
  if (diff === 1) return 'TOMORROW';
  return date.toLocaleDateString('en-US', { weekday: 'long' }).toUpperCase();
}

function readSettings(params) {
  const rotate = parseInt(params.get('rotate'), 10);
  return {
    rotateSecs: Number.isNaN(rotate) ? 12 : Math.min(60, Math.max(3, rotate)),
    showQR: params.get('qr') !== '0',
    hideEmpty: params.get('empty') === 'hide',
    showTicker: params.get('ticker') === '1'
  };
}

function EventsTv() {
  const [params] = useSearchParams();
  const settings = useMemo(() => readSettings(params), [params]);
  // The last live data this browser saw, so a reload with the server down
  // still shows the week, marked as not live (utils/tvSnapshot.js).
  const [snapshot] = useState(() => readTvSnapshot(SNAPSHOT_KEY));
  const [events, setEvents] = useState(() => snapshot?.data.events ?? null);
  const [error, setError] = useState(null);
  const [tick, setTick] = useState(0);
  // Re-read the clock every minute so the rows move on at midnight without a reload.
  const [now, setNow] = useState(() => new Date());
  const { stale, asOf, noteResponses } = useTvFreshness(snapshot?.savedAt ?? null);
  const stage = useTvStage(MIN_STAGE_WIDTH);

  const fetchData = useCallback(async () => {
    try {
      const response = await apiService.getTvEvents();
      setEvents(response.data.events);
      if (noteResponses([response])) writeTvSnapshot(SNAPSHOT_KEY, response.data);
      setError(null);
    } catch (err) {
      setError(tvLoadErrorMessage(err, 'Unable to load events.'));
    }
  }, [noteResponses]);

  useEffect(() => {
    fetchData();
    const intervalId = setInterval(fetchData, REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [fetchData]);

  // The server is back: reload now rather than at the next refresh.
  useOnReconnect(fetchData);

  useEffect(() => {
    const intervalId = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const intervalId = setInterval(() => setTick((value) => value + 1), settings.rotateSecs * 1000);
    return () => clearInterval(intervalId);
  }, [settings.rotateSecs]);

  const today = startOfDay(now);
  const todayKey = today.getTime();

  const board = useMemo(() => {
    const weekStart = new Date(todayKey);
    const weekEnd = new Date(todayKey + 7 * DAY);

    // event_date is club-local wall time with no zone, which Date reads as local. Private
    // events are in the feed only for the hallway sign's host card, never listed here.
    const upcoming = (events || [])
      .filter((event) => !event.is_private)
      .map((event) => ({ ...event, date: new Date(event.event_date) }))
      .filter((event) => event.date >= weekStart)
      .sort((a, b) => a.date - b.date);
    const thisWeek = upcoming.filter((event) => event.date < weekEnd);
    const later = upcoming.filter((event) => event.date >= weekEnd).slice(0, TICKER_LENGTH);

    const days = Array.from({ length: 7 }, (_, i) => {
      const day = new Date(todayKey + i * DAY);
      const dayEvents = thisWeek.filter((event) => event.date.toDateString() === day.toDateString());
      return {
        key: day.toDateString(),
        isToday: i === 0,
        dow: i === 0 ? 'TODAY' : day.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase(),
        num: day.getDate(),
        events: dayEvents
      };
    });

    return {
      weekStart,
      lastDay: new Date(todayKey + 6 * DAY),
      thisWeek,
      // Nothing on this week: feature the next thing that is, rather than an empty panel.
      pool: thisWeek.length > 0 ? thisWeek : upcoming.slice(0, 1),
      days,
      later
    };
  }, [events, todayKey]);

  if (!events) {
    return (
      <div className="evtv-root">
        <div className="evtv-boot">{error || 'Loading this week at the club...'}</div>
      </div>
    );
  }

  const { pool, thisWeek, later } = board;
  const featureIndex = pool.length > 0 ? tick % pool.length : 0;
  const feature = pool[featureIndex];
  const featureImage = feature ? eventImage(feature) : null;
  const featureHosts = feature?.host_names?.length ? ` · Hosted by ${feature.host_names.join(', ')}` : '';
  const days = settings.hideEmpty ? board.days.filter((day) => day.events.length > 0) : board.days;
  const savedAt = stale && asOf ? formatSavedAt(asOf, now) : null;

  return (
    <div className="evtv-root">
      <div
        className="evtv-stage"
        style={{
          width: `${stage.width}px`,
          transform: `translate(-50%, -50%) scale(${stage.scale})`
        }}
      >
        <header className="evtv-header">
          <div className="evtv-header__brand">
            <img className="evtv-header__logo" src="/logo.png" alt="Capital Pinball" />
            <div>
              <p className="evtv-header__eyebrow">CAPITAL PINBALL CLUB</p>
              <p className="evtv-header__title">This week at the club</p>
            </div>
          </div>
          <div className="evtv-header__meta">
            <span className="evtv-header__range">
              {shortDate(board.weekStart)} - {shortDate(board.lastDay)}
            </span>
            <span className="evtv-header__count">
              {thisWeek.length} {thisWeek.length === 1 ? 'event' : 'events'} in the next 7 days
            </span>
            {savedAt && (
              <span className="evtv-header__offline">
                <span className="evtv-dot" />Offline · as of {savedAt}
              </span>
            )}
          </div>
        </header>

        <div className="evtv-body">
          <section className="evtv-feature">
            <div
              className={`evtv-feature__art ${featureImage ? 'has-image' : ''}`}
              style={featureImage ? { backgroundImage: `url("${featureImage}")` } : undefined}
            >
              {!featureImage && feature && (
                <span className="evtv-feature__mono">{monogram(feature.title)}</span>
              )}
              <div className="evtv-feature__overlay">
                {feature ? <span className="evtv-feature__when">{whenLabel(feature.date, today)}</span> : <span />}
                {pool.length > 1 && (
                  <span className="evtv-dots">
                    {pool.map((event, i) => (
                      <span key={event.id} className={`evtv-dots__dot ${i === featureIndex ? 'is-on' : ''}`} />
                    ))}
                  </span>
                )}
              </div>
            </div>

            <div className="evtv-feature__body">
              {feature ? (
                <>
                  <p className="evtv-feature__date">
                    {feature.date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                    {' · '}
                    {timeLabel(feature.date)}
                  </p>
                  <p className="evtv-feature__title">{feature.title}</p>
                  <p className="evtv-feature__meta">{getCategoryLabel(feature.event_category)}{featureHosts}</p>
                  {feature.description && <p className="evtv-feature__desc">{feature.description}</p>}
                </>
              ) : (
                <p className="evtv-feature__title">Nothing scheduled this week</p>
              )}
            </div>

            {settings.showQR && feature && (
              <div className="evtv-qr">
                <div className="evtv-qr__code">
                  <QRCode
                    value={`${window.location.origin}/events/${feature.id}/rsvp`}
                    size={92}
                    level="M"
                  />
                </div>
                <div className="evtv-qr__text">
                  <p className="evtv-qr__title">Scan to RSVP</p>
                  <p className="evtv-qr__sub">Opens this event in the Capital Pinball Club application.</p>
                </div>
              </div>
            )}
          </section>

          <section className="evtv-days">
            {days.map((day) => (
              <div className={`evtv-day ${day.isToday ? 'is-today' : ''}`} key={day.key}>
                <div className="evtv-day__date">
                  <span className="evtv-day__dow">{day.dow}</span>
                  <span className="evtv-day__num">{day.num}</span>
                </div>
                <div className="evtv-day__events">
                  {day.events.map((event) => (
                    <div className="evtv-day__event" key={event.id}>
                      <span className="evtv-day__time">{timeLabel(event.date)}</span>
                      <span className="evtv-day__title">{event.title}</span>
                    </div>
                  ))}
                  {day.events.length === 0 && <span className="evtv-day__empty">No events</span>}
                </div>
              </div>
            ))}
          </section>
        </div>

        {settings.showTicker && later.length > 0 && (
          <footer className="evtv-ticker">
            <div className="evtv-ticker__label">COMING UP</div>
            <div className="evtv-ticker__viewport">
              <div className="evtv-ticker__track">
                {[0, 1].map((copy) => (
                  <div className="evtv-ticker__run" key={copy} aria-hidden={copy === 1}>
                    {later.map((event) => (
                      <span className="evtv-ticker__item" key={`${copy}-${event.id}`}>
                        <span className="evtv-ticker__day">
                          {event.date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                        </span>
                        <span className="evtv-ticker__title">{event.title}</span>
                        <span className="evtv-ticker__time">{timeLabel(event.date)}</span>
                      </span>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </footer>
        )}
      </div>
    </div>
  );
}

export default EventsTv;
